import {ProductDataStore} from "../../stores/productDataStore";
import {OrnamentGlassRuleService} from "../../services/ornamentGlassRuleService";
import {GlassRuleService} from "../../services/glassRuleService";
import {AppStateService} from "../../services/appStateService";
import {UIService} from "../UIService";
import {PriceFormatter} from "../../services/priceFormatter";
import {DrawingDataService} from "../drawingComponent/drawingDataService";
import {CustomerTypeService} from "../../services/customerTypeService";
import {PriceService} from "../../services/priceService";

export class GlazingService {
    private productDataStore: ProductDataStore;
    private ornamentGlassRuleService: OrnamentGlassRuleService;
    private glassRuleService: GlassRuleService;
    private appStateService: AppStateService;
    private uiService: UIService;
    private customerTypeService: CustomerTypeService;
    private priceService: PriceService;
    private priceFormatter: PriceFormatter;
    private drawingsDataService: DrawingDataService;

    constructor() {
        this.productDataStore = new ProductDataStore();
        this.ornamentGlassRuleService = new OrnamentGlassRuleService();
        this.glassRuleService = new GlassRuleService();
        this.appStateService = new AppStateService();
        this.uiService = new UIService();
        this.customerTypeService = new CustomerTypeService();
        this.priceService = new PriceService();
        this.priceFormatter = new PriceFormatter();
        this.drawingsDataService = new DrawingDataService();
    }

    public resetSelectedOptions() {
        let initialInsideGlassId = this.productDataStore.getInitialOptionByName('insideGlass').id;
        let initialOutsideGlassId = this.productDataStore.getInitialOptionByName('outsideGlass').id;
        let initialOrnamentId = this.productDataStore.getInitialOptionByName('ornament').id;

        this.productDataStore.updateSelectedOption('insideGlass', initialInsideGlassId);
        this.productDataStore.updateSelectedOption('outsideGlass', initialOutsideGlassId);
        this.productDataStore.updateSelectedOption('ornament', initialOrnamentId);

        this.uiService.resetSelectionsByPartName('insideGlass');
        this.uiService.resetSelectionsByPartName('outsideGlass');
        this.uiService.resetSelectionsByPartName('ornament');

        this.uiService.setSelectedOption(this.uiService.getElementByPartNameAndId('insideGlass', initialInsideGlassId));
        this.uiService.setSelectedOption(this.uiService.getElementByPartNameAndId('outsideGlass', initialOutsideGlassId));
        this.uiService.setSelectedOption(this.uiService.getElementByPartNameAndId('ornament', initialOrnamentId));
    }

    public disableOrnamentOption() {
        let selectedInsideGlassId = this.productDataStore.getSelectedOptionIdByName('insideGlass');
        let selectedInsideGlassData = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedInsideGlassId);

        return this.isProductEligibleForOrnamentGlassRule() &&
            this.ornamentGlassRuleService.checkIfLaminatedOrSafetyGlassOnInside(selectedInsideGlassData);
    }

    public disableInsideGlassOption(insideGlassId) {
        let selectedOrnamentId = this.productDataStore.getSelectedOptionIdByName('ornament');
        let selectedOrnamentData = this.productDataStore.getAvailableOptionByNameAndId('ornament', selectedOrnamentId);

        let selectedOutsideGlassId = this.productDataStore.getSelectedOptionIdByName('outsideGlass');
        let selectedOutsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedOutsideGlassId);

        let insideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', insideGlassId);

        return (
            (
                this.isProductEligibleForOrnamentGlassRule() &&
                this.ornamentGlassRuleService.checkIfLaminatedOrSafetyGlassOnInside(insideGlass) &&
                this.ornamentGlassRuleService.checkIfOrnamentOnInside(selectedOrnamentData)
            ) ||
            (
                this.isProductEligibleForGlazingRule() &&
                this.glassRuleService.hideInsideGlass(insideGlass, selectedOutsideGlass)
            )
        );
    }

    public disableOutsideGlassOption(outsideGlassId) {
        let selectedInsideGlassId = this.productDataStore.getSelectedOptionIdByName('insideGlass');
        let selectedInsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedInsideGlassId);
        let outsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', outsideGlassId);

        return this.isProductEligibleForGlazingRule() &&
            this.glassRuleService.hideOutsideGlass(outsideGlass, selectedInsideGlass);
    }

    public refreshComponentTemplate() {
        this.appStateService.setAppSettingTemplateFlag('glazing', 'glazingRuleNotice', this.isGlazingRuleActive());
        this.appStateService.setAppSettingTemplateFlag('glazing', 'ornamentGlassRuleNotice', this.isOrnamentGlassRuleActive());
        this.uiService.refreshElementsInModule('glazing');
    }

    private isOrnamentGlassRuleActive(): boolean
    {
        const selectedInsideGlassId = this.productDataStore.getSelectedOptionIdByName('insideGlass');
        const selectedInsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedInsideGlassId);
        const selectedOrnamentId = this.productDataStore.getSelectedOptionIdByName('ornament');
        const selectedOrnament = this.productDataStore.getAvailableOptionByNameAndId('ornament', selectedOrnamentId);

        return this.isProductEligibleForOrnamentGlassRule() &&
            (
                this.ornamentGlassRuleService.checkIfLaminatedOrSafetyGlassOnInside(selectedInsideGlass) ||
                this.ornamentGlassRuleService.checkIfOrnamentOnInside(selectedOrnament)
            );
    }

    private isGlazingRuleActive(): boolean
    {
        const selectedInsideGlassId = this.productDataStore.getSelectedOptionIdByName('insideGlass');
        const selectedInsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedInsideGlassId);
        const selectedOutsideGlassId = this.productDataStore.getSelectedOptionIdByName('outsideGlass');
        const selectedOutsideGlass = this.productDataStore.getAvailableOptionByNameAndId('glass', selectedOutsideGlassId);

        return this.isProductEligibleForGlazingRule() &&
            this.glassRuleService.areConditionsMet(
                selectedInsideGlass,
                selectedOutsideGlass
            );
    }

    public isRoofWindow() {
        return this.productDataStore.getProductType() === 'roofWindow';
    }

    private isProductEligibleForOrnamentGlassRule()
    {
        return this.productDataStore.getProductInfoAttributeByName('isTwoGlazingProduct');
    }

    private isProductEligibleForGlazingRule()
    {
        return this.productDataStore.getProductInfoAttributeByName('productEligibleForGlazingRule');
    }

    public getGlazingPrice(priceAfterDiscount: number): string {
        if (this.customerTypeService.isB2bCustomer()) {
            priceAfterDiscount = this.priceService.calculateNetPrice(priceAfterDiscount);
        }

        return this.priceFormatter.getPriceWithCurrency(this.drawingsDataService.getGlassSquareMeterArea() * priceAfterDiscount, true) + ' (' +
            this.priceFormatter.getPriceWithCurrency(priceAfterDiscount, false, true) + ')';
    }
}
